import React, { useState, useEffect, useRef, useCallback } from "react";
import "./ReceiptUpload.css";
import ActionButton from "../components/ActionButton.tsx";
import { PiPlus, PiSpinnerGap, PiEnvelope, PiWarning } from "react-icons/pi";
import EditReceiptForm from "../components/EditReceiptForm.tsx";
import UploadArea from "../components/right-sidebar/UploadArea.tsx";
import InfoBanner from "../components/InfoBanner.tsx";
import { supabase } from "../SupabaseClient.tsx";
import { useNavigate } from "react-router-dom";
import {
  saveFileToStorage,
  updateFileOrderInStorage,
  getFilesFromStorage,
  removeFileFromStorage,
  clearAllFilesFromStorage,
} from "../utils/uploadStorage.ts";
import {
  BeneficiarySettings,
  addMissingBeneficiaries,
  fetchBeneficiarySettings,
  getDefaultBeneficiary,
  matchBeneficiary,
} from "../utils/beneficiaries.ts";
import { countDraftReceipts, viewDraftReceipts } from "../utils/draftReceipts.ts";

// Define interface for AI response
interface AiReceiptData {
  vendor: string;
  purchase_id: string;
  purchase_date: string;
  purchase_amount: number;
  item_category: string;
  category: string;
  beneficiary?: string;
}

// Define interface for uploaded file info
interface UploadedFileInfo {
  id: string;
  file: File;
  order: any | null;
  previewUrl: string;
  fileName: string;
  processing: boolean;
  error: string | null;
}

// The client reports every failed call as "non-2xx status code". A 4xx response
// says what the user can fix; anything else isn't worth showing them.
async function getProcessingErrorMessage(error: any): Promise<string> {
  const response = error?.context;
  if (response?.status >= 400 && response?.status < 500) {
    try {
      const body = await response.json();
      if (body?.error) return body.error;
    } catch {
      // No readable reason in the response
    }
  }
  return "We couldn't read this receipt. Please try again.";
}

const ReceiptUpload = () => {
  const navigate = useNavigate();
  // State to track uploaded files and their temporary orders
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileInfo[]>([]);
  const [duplicateFilenames, setDuplicateFilenames] = useState<Set<string>>(
    new Set()
  );
  const [isAddingToLibrary, setIsAddingToLibrary] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [draftCount, setDraftCount] = useState(0);
  const hasRestoredFiles = useRef(false);

  // Beneficiaries used to match the name on a receipt, loaded once and shared
  // by every file being processed (including files restored on mount)
  const beneficiarySettingsRef = useRef<Promise<BeneficiarySettings> | null>(
    null
  );
  const loadBeneficiarySettings = useCallback(() => {
    if (!beneficiarySettingsRef.current) {
      beneficiarySettingsRef.current = fetchBeneficiarySettings();
    }
    return beneficiarySettingsRef.current;
  }, []);

  // Process a single file with AI
  const processFileWithAI = useCallback(async (fileInfo: UploadedFileInfo) => {
    try {
      // Mark this file as processing, clear any previous error
      setUploadedFiles((prevFiles) =>
        prevFiles.map((item) =>
          item.id === fileInfo.id ? { ...item, processing: true, error: null } : item
        )
      );

      // Update processing state in IndexedDB
      await updateFileOrderInStorage(fileInfo.id, fileInfo.order, true, null);

      const { beneficiaries, accountHolderName } =
        await loadBeneficiarySettings();

      // Prepare form data
      const formData = new FormData();
      formData.append("file", fileInfo.file);
      formData.append("beneficiaries", JSON.stringify(beneficiaries));
      if (accountHolderName) {
        formData.append("accountHolderName", accountHolderName);
      }

      // Call the Supabase Edge Function with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60s timeout

      let data, error;
      try {
        const result = await supabase.functions.invoke("process-file", {
          body: formData,
        });
        data = result.data;
        error = result.error;
      } catch (invokeError: any) {
        clearTimeout(timeoutId);
        if (invokeError.name === "AbortError") {
          throw new Error("Request timed out. Please try again.");
        }
        throw invokeError;
      }
      clearTimeout(timeoutId);

      if (error) {
        const errorMessage = await getProcessingErrorMessage(error);
        console.error("Error processing file:", error);

        setUploadedFiles((prevFiles) =>
          prevFiles.map((item) =>
            item.id === fileInfo.id
              ? { ...item, processing: false, error: errorMessage }
              : item
          )
        );
        await updateFileOrderInStorage(fileInfo.id, fileInfo.order, false, errorMessage);
        return;
      }

      console.log("File processed successfully:", data);

      // Create a temporary order object from AI data
      const aiData = data as AiReceiptData;

      // Create an order object, handling both valid data and empty/missing data cases
      const order = {
        id: fileInfo.id,
        name: aiData?.item_category || null,
        merchant: aiData?.vendor || null,
        category: aiData?.category || null,
        purchase_id: aiData?.purchase_id || null,
        purchase_date: aiData?.purchase_date || null,
        amount: aiData?.purchase_amount || null,
        origin: { name: "Manual Upload" },
        status: {
          name:
            // If we have valid AI data with required fields
            aiData && aiData.purchase_date && aiData.purchase_amount
              ? "Ready to Review"
              : // If AI returned no data or incomplete data
              "Needs Details",
        },
        original_filename: fileInfo.fileName,
        // Use the beneficiary named on the receipt if it's in the user's list
        beneficiary:
          matchBeneficiary(aiData?.beneficiary, beneficiaries) ??
          getDefaultBeneficiary(beneficiaries),
        notes: null,
      };

      // Update the file info with the new order
      setUploadedFiles((prevFiles) =>
        prevFiles.map((item) =>
          item.id === fileInfo.id
            ? { ...item, order, processing: false, error: null }
            : item
        )
      );

      // Save updated order to IndexedDB
      await updateFileOrderInStorage(fileInfo.id, order, false, null);
    } catch (error: any) {
      console.error("Unexpected error processing file:", error);
      const errorMessage = error?.message || "An unexpected error occurred. Please try again.";

      setUploadedFiles((prevFiles) =>
        prevFiles.map((item) =>
          item.id === fileInfo.id
            ? { ...item, processing: false, error: errorMessage }
            : item
        )
      );

      // Update error state in IndexedDB
      await updateFileOrderInStorage(fileInfo.id, fileInfo.order, false, errorMessage);
    }
  }, [loadBeneficiarySettings]);

  // Retry processing for a failed file
  const handleRetryProcessing = useCallback((id: string) => {
    const fileInfo = uploadedFiles.find((f) => f.id === id);
    if (fileInfo) {
      processFileWithAI(fileInfo);
    }
  }, [uploadedFiles, processFileWithAI]);

  // Restore files from IndexedDB on component mount
  useEffect(() => {
    async function restoreFilesFromStorage() {
      if (hasRestoredFiles.current) return;
      hasRestoredFiles.current = true;

      try {
        const storedFiles = await getFilesFromStorage();
        if (storedFiles.length > 0) {
          setUploadedFiles(storedFiles);

          // Check for duplicate filenames for restored files
          const filenames = storedFiles.map((file) => file.fileName);
          const duplicates = await checkDuplicateFilenames(filenames);
          setDuplicateFilenames(duplicates);

          // Re-process any files that were still processing when the page was left
          // (but not files that already have an error — let user manually retry those)
          storedFiles
            .filter((file) => (file.processing || !file.order) && !file.error)
            .forEach((fileInfo) => {
              processFileWithAI(fileInfo);
            });
        }
      } catch (error) {
        console.error("Error restoring files from storage:", error);
      }
    }
    restoreFilesFromStorage();
  }, [processFileWithAI]);

  // Fetch user email on component mount
  useEffect(() => {
    async function getUserEmail() {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        setUserEmail(user.email ?? null);
      }
    }
    getUserEmail();
  }, []);

  // Fetch the number of email uploaded receipts waiting for review
  useEffect(() => {
    countDraftReceipts().then(setDraftCount);
  }, []);

  // Handle file upload from the upload button
  const handleUpload = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = ".pdf,.jpg,.jpeg,.png";

    input.onchange = (e) => {
      const selectedFiles = (e.target as HTMLInputElement).files;
      if (selectedFiles) {
        addFilesToUploadList(selectedFiles);
      }
    };

    input.click();
  };

  // Handle files from UploadReceipt component
  const handleFilesFromUploadComponent = (files: FileList | null) => {
    if (files) {
      addFilesToUploadList(files);
    }
  };

  // Check for duplicate filenames in the database
  const checkDuplicateFilenames = async (filenames: string[]) => {
    try {
      // Query order_attachments table for duplicate filenames
      const { data, error } = await supabase
        .from("order_attachments")
        .select("original_filename")
        .in("original_filename", filenames);

      if (error) {
        console.error("Error checking duplicate filenames:", error);
        return new Set<string>();
      }

      return new Set(
        data
          ?.map((attachment) => attachment.original_filename)
          .filter(Boolean) || []
      );
    } catch (error) {
      console.error("Unexpected error checking duplicates:", error);
      return new Set<string>();
    }
  };

  // Common function to add files to the upload list
  const addFilesToUploadList = async (files: FileList) => {
    const newFiles = Array.from(files).map((file) => {
      // Generate a unique ID for the file
      const id = crypto.randomUUID();

      // Create a temporary URL for the file preview
      const previewUrl = URL.createObjectURL(file);

      return {
        id,
        file,
        order: null,
        previewUrl,
        fileName: file.name,
        processing: false,
        error: null,
      };
    });

    setUploadedFiles((prev) => [...prev, ...newFiles]);

    // Save each new file to IndexedDB
    for (const fileInfo of newFiles) {
      await saveFileToStorage(
        fileInfo.id,
        fileInfo.file,
        fileInfo.order,
        fileInfo.processing
      );
    }

    // Check for duplicate filenames in the database
    const filenames = newFiles.map((file) => file.fileName);
    const duplicates = await checkDuplicateFilenames(filenames);
    setDuplicateFilenames(
      (prevDuplicates) => new Set([...prevDuplicates, ...duplicates])
    );

    // Process each new file with AI
    newFiles.forEach((fileInfo) => {
      processFileWithAI(fileInfo);
    });
  };

  // Remove a file from the upload list
  const handleRemoveFile = async (id: string) => {
    // Remove from IndexedDB
    await removeFileFromStorage(id);

    setUploadedFiles((prev) => {
      const filtered = prev.filter((item) => item.id !== id);
      // Clean up object URLs
      const itemToRemove = prev.find((item) => item.id === id);
      if (itemToRemove && itemToRemove.previewUrl) {
        URL.revokeObjectURL(itemToRemove.previewUrl);

        // Remove from duplicates if no other files have the same name
        const hasOtherFilesWithSameName = filtered.some(
          (file) => file.fileName === itemToRemove.fileName
        );
        if (!hasOtherFilesWithSameName) {
          setDuplicateFilenames((prevDuplicates) => {
            const newDuplicates = new Set(prevDuplicates);
            newDuplicates.delete(itemToRemove.fileName);
            return newDuplicates;
          });
        }
      }
      return filtered;
    });
  };

  // Save all receipt data to the database
  const handleAddToLibrary = async () => {
    try {
      setIsAddingToLibrary(true);

      // Only process files that have been fully processed by AI
      const filesToSave = uploadedFiles.filter(
        (file) => file.order && !file.processing
      );

      if (filesToSave.length === 0) {
        console.log("No ready files to save");
        setIsAddingToLibrary(false);
        return;
      }

      // Create an array of promises, one for each file to be processed
      const uploadPromises = filesToSave.map(async (fileInfo) => {
        try {
          // Insert the order data to get an ID
          const { data, error } = await supabase
            .from("orders")
            .insert({
              name: fileInfo.order.name,
              merchant: fileInfo.order.merchant,
              category: fileInfo.order.category,
              purchase_id: fileInfo.id,
              purchase_date: fileInfo.order.purchase_date,
              amount: fileInfo.order.amount,
              beneficiary: fileInfo.order.beneficiary,
              notes: fileInfo.order.notes,
              status_id: 2, // Saved status
              origin_id: 2, // Manual Upload
              receipt_captured: true,
            })
            .select("id, user_id")
            .single();

          if (error) {
            console.error("Error inserting order:", error);
            return { success: false, fileInfo, error };
          }

          if (data) {
            // Now that we have the order ID and user ID, create a storage path
            const storagePath = `${data.user_id}/${data.id}.pdf`;

            // Upload the file to storage
            const { error: uploadError } = await supabase.storage
              .from("order_receipts")
              .upload(storagePath, fileInfo.file, {
                contentType: fileInfo.file.type,
                upsert: true,
              });

            if (uploadError) {
              console.error("Error uploading file to storage:", uploadError);
              return { success: false, fileInfo, error: uploadError };
            }

            // Create attachment record in order_attachments table
            const { error: attachmentError } = await supabase
              .from("order_attachments")
              .insert({
                order_id: data.id,
                storage_path: storagePath,
                original_filename: fileInfo.fileName,
                mime_type: fileInfo.file.type,
                is_primary: true,
              });

            if (attachmentError) {
              console.error(
                "Error creating attachment record:",
                attachmentError
              );
              return { success: false, fileInfo, error: attachmentError };
            }

            return { success: true, fileInfo, orderId: data.id };
          }
          return {
            success: false,
            fileInfo,
            error: "No data returned from upsert",
          };
        } catch (error) {
          console.error("Error processing file:", error);
          return { success: false, fileInfo, error };
        }
      });

      // Wait for all uploads to complete
      const results = await Promise.all(uploadPromises);

      // Log results
      const successCount = results.filter((r) => r.success).length;
      console.log(
        `Successfully processed ${successCount} of ${results.length} files`
      );

      // Save any newly typed beneficiaries so they show up in the dropdown next time
      await addMissingBeneficiaries(
        results
          .filter((r) => r.success)
          .map((r) => r.fileInfo.order.beneficiary)
      );

      // Clear all files from IndexedDB after successful upload
      await clearAllFilesFromStorage();

      setIsAddingToLibrary(false);

      // Navigate to receipt library after upload attempt (even if some failed)
      navigate("/receipt-library");
    } catch (error) {
      setIsAddingToLibrary(false);
      console.error("Unexpected error saving files:", error);
    }
  };

  const enableAddToLibrary = () => {
    // Count receipts that are "Ready to Review"
    const readyToSaveFiles = uploadedFiles.filter(
      (file) =>
        file.order &&
        file.order.status?.name === "Ready to Review" &&
        !file.processing
    );

    // Count files with issues (still processing, errored, or not ready)
    const filesWithIssues = uploadedFiles.filter(
      (file) =>
        file.processing ||
        file.error ||
        !file.order ||
        file.order.status?.name !== "Ready to Review"
    );

    // Return true only if we have at least one ready file AND no files with issues
    return readyToSaveFiles.length > 0 && filesWithIssues.length === 0;
  };

  const handleFormChange = async (id: string, updatedValues: any) => {
    let updatedOrder: any = null;

    setUploadedFiles((prevFiles) =>
      prevFiles.map((item) => {
        if (item.id === id && item.order) {
          // Check if required fields are filled
          const hasRequiredFields =
            updatedValues.purchase_date && updatedValues.amount;

          // Update the order with the new values
          updatedOrder = {
            ...item.order,
            name: updatedValues.name,
            merchant: updatedValues.merchant,
            category: updatedValues.category,
            purchase_date: updatedValues.purchase_date,
            amount: updatedValues.amount,
            beneficiary: updatedValues.beneficiary,
            notes: updatedValues.notes,
            // Update status based on required fields
            status: {
              name: hasRequiredFields ? "Ready to Review" : "Needs Details",
            },
          };

          return {
            ...item,
            order: updatedOrder,
          };
        }
        return item;
      })
    );

    // Persist the updated order to IndexedDB
    if (updatedOrder) {
      await updateFileOrderInStorage(id, updatedOrder, false);
    }
  };

  return (
    <div className="global-page-screen">
      <div className="page">
        <div className="page-header">
          <div className="page-header-title">
            <h1>Receipt Upload</h1>
            <p>Upload receipts and we'll read the details for you.</p>
          </div>
          <ActionButton
            icon={<PiPlus />}
            text="Upload Receipt"
            onClick={handleUpload}
            responsive={true}
          />
        </div>

        {/* Email uploaded receipts waiting for review */}
        {draftCount > 0 && (
          <InfoBanner
            icon={<PiWarning size={24} />}
            color="yellow"
            ctaText={"Review Receipt" + (draftCount === 1 ? "" : "s")}
            onCtaClick={() => viewDraftReceipts(navigate)}
          >
            <p>
              You have <strong>{draftCount} email uploaded</strong>
              {draftCount !== 1 ? " receipts" : " receipt"} that{" "}
              {draftCount === 1 ? "needs" : "need"} detail confirmation.
            </p>
          </InfoBanner>
        )}

        {/* Email upload informational banner */}
        <InfoBanner
          icon={<PiEnvelope size={24} />}
          color="blue"
          ctaText="Add Emails"
          onCtaClick={() => navigate("/settings")}
        >
          <p>
            You can also upload receipts via email by sending them to{" "}
            <strong>upload@shoebox.io</strong> from{" "}
            <strong>{userEmail || "your account email"}</strong>. Add other
            authorized emails in Settings.
          </p>
        </InfoBanner>

        <div className="upload-receipt-upload-area">
          <UploadArea
            showTitle={false}
            onFilesSelected={handleFilesFromUploadComponent}
          />
        </div>
        <div className="page-header">
          <div className="page-header-title">
            <h2>Uploaded Receipts</h2>
            <p>
              {uploadedFiles.length > 0
                ? `${uploadedFiles.length} receipt${uploadedFiles.length !== 1 ? "s" : ""
                } ready for review`
                : "Upload a receipt to get started."}
            </p>
          </div>
          <ActionButton
            icon={
              isAddingToLibrary ? (
                <PiSpinnerGap className="spin-animation" />
              ) : (
                <PiPlus />
              )
            }
            text={`Add to Library`}
            onClick={handleAddToLibrary}
            responsive={true}
            disabled={!enableAddToLibrary() || isAddingToLibrary}
          />
        </div>

        {/* AI accuracy warning banner - only show when receipts are uploaded */}
        {uploadedFiles.length > 0 && (
          <InfoBanner icon={<PiWarning size={24} />} color="yellow">
            <p>
              Please verify all receipt information. Our AI does its best, but
              may make mistakes when extracting data from your receipts.
            </p>
          </InfoBanner>
        )}

        <div className="upload-receipt-section">
          {uploadedFiles.map((item) => (
            <EditReceiptForm
              key={item.id}
              order={item.order}
              receiptUrl={item.previewUrl}
              fileName={item.fileName}
              detailView={false}
              onDelete={() => handleRemoveFile(item.id)}
              onFormChange={handleFormChange}
              duplicateFileName={duplicateFilenames.has(item.fileName)}
              processing={item.processing}
              processingError={item.error}
              onRetry={() => handleRetryProcessing(item.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReceiptUpload;
